import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from './Footer';
import './sensors.css';
import { API, authFetch } from '../api';

const HR_WINDOW_MS = 5 * 60 * 1000;     // chart window — last 5 minutes
const HR_REFRESH_MS = 5 * 1000;          // poll HR window every 5s
const STRESS_REFRESH_MS = 30 * 1000;     // poll stress score every 30s

// ─── ICONS ───

function PhoneIcon({ size = 28, color = '#5CB89A' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="6" y="2" width="12" height="20" rx="3" />
      <line x1="11" y1="18" x2="13" y2="18" />
    </svg>
  );
}

function ConnectedDot() {
  return <span className="sensors-connected-dot" />;
}

function HeartPulseIcon({ size = 18, color = '#FF6B6B' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
    </svg>
  );
}

function HelpIcon({ size = 16, color = '#FFFFFF' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// ─── BACKEND FETCH HELPERS ───
//
// Both endpoints are read-only from the web's perspective. Mobile is
// the one publishing HR samples and the stress score; web just reads
// what's there. authFetch handles bearer-token + refresh, same as the
// rest of the app.

/**
 * Defensive sample parser — HrPoint's field names aren't pinned down
 * on the backend yet. Accepts {timestamp, hr}, {time, value},
 * {t, heartRate}, and a few common variants so a small Java rename
 * doesn't force a frontend redeploy. Returns null when nothing
 * usable is in the row.
 */
function normaliseHrSample(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const timestamp = Number(raw.timestamp ?? raw.time ?? raw.t ?? raw.ts);
  const hr = Number(raw.hr ?? raw.heartRate ?? raw.value ?? raw.bpm);
  if (!Number.isFinite(timestamp) || !Number.isFinite(hr)) return null;
  return { timestamp, hr };
}

async function fetchHrWindow() {
  try {
    const res = await authFetch(API.sensorHrWindow(), { method: 'GET' });
    if (!res.ok) return null;
    const data = await res.json();
    if (!Array.isArray(data)) return null;
    return data
      .map(normaliseHrSample)
      .filter((sample) => sample !== null)
      // Keep only the last HR_WINDOW_MS worth so the chart x-axis stays
      // a clean 5-minute window even if backend returns more.
      .filter((sample, _, all) => {
        const tNewest = all[all.length - 1].timestamp;
        return tNewest - sample.timestamp <= HR_WINDOW_MS;
      });
  } catch (err) {
    console.error('HR window fetch failed:', err);
    return null;
  }
}

async function fetchStressScore() {
  try {
    const res = await authFetch(API.sensorStressScore(), { method: 'GET' });
    if (!res.ok) return null;
    const data = await res.json();
    const parsed = Number(data?.stressScore);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (err) {
    console.error('Stress score fetch failed:', err);
    return null;
  }
}

// ─── LIVE HR CHART ───

function LiveHrChart({ data, width = 560, height = 220 }) {
  const geometry = useMemo(() => {
    if (!data || data.length < 2) return null;

    const padTop = 18;
    const padBottom = 30;
    const padLeft = 12;
    const padRight = 12;

    const plotW = width - padLeft - padRight;
    const plotH = height - padTop - padBottom;

    const hrs = data.map((d) => d.hr);
    const minHr = Math.min(...hrs);
    const maxHr = Math.max(...hrs);
    const latestHr = data[data.length - 1].hr;

    // Grafik görsel olarak 0'dan başlasın.
    // Böylece 78 bpm grafiğin en altı gibi görünmez.
    const yAxisMin = 0;

    // Üst limit sabit 120 olsun; nabız 120'yi geçerse 10'luk basamakla genişlesin.
    const yAxisMax = Math.max(120, Math.ceil(maxHr / 10) * 10);

    const hrRange = Math.max(yAxisMax - yAxisMin, 1);

    const newestTimestamp = data[data.length - 1].timestamp;
    const windowStart = newestTimestamp - HR_WINDOW_MS;

    const points = data
      .map((sample) => {
        const x =
          padLeft +
          ((sample.timestamp - windowStart) / HR_WINDOW_MS) * plotW;

        const normalizedHr = Math.max(
          yAxisMin,
          Math.min(sample.hr, yAxisMax)
        );

        const y =
          padTop +
          (1 - (normalizedHr - yAxisMin) / hrRange) * plotH;

        return `${x},${y}`;
      })
      .join(' ');

    return {
      points,
      minHr,
      maxHr,
      latestHr,
      yAxisMin,
      yAxisMax,
      padTop,
      padBottom,
      padLeft,
      padRight,
      plotW,
      plotH,
    };
  }, [data, width, height]);



  if (!geometry) {
    return (
      <div className="sensors-chart-empty">
        No heart-rate samples yet — start a measurement from the HRV-4 mobile app.
      </div>
    );
  }

  return (
    <>
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" className="sensors-chart-svg">
        {/* Horizontal grid lines */}
        {[0, 1, 2, 3, 4].map(i => {
          const y = geometry.padTop + (i / 4) * geometry.plotH;
          return (
            <line
              key={i}
              x1={geometry.padLeft}
              y1={y}
              x2={width - geometry.padRight}
              y2={y}
              stroke="#DDE4EA"
              strokeWidth="1"
            />
          );
        })}

        {/* Trend line */}
        <polyline
          points={geometry.points}
          fill="none"
          stroke="#FF6B6B"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Time axis labels */}
        <text x={geometry.padLeft} y={height - 8} fontSize="11" fill="#8A94A1">-5m</text>
        <text x={width - geometry.padRight} y={height - 8} fontSize="11" fill="#8A94A1" textAnchor="end">now</text>
      </svg>

      <div className="sensors-chart-range">
        <span>Min {Math.round(geometry.minHr)} bpm</span>
        <span>Max {Math.round(geometry.maxHr)} bpm</span>
      </div>
    </>
  );
}

// ─── MAIN ───

function Sensors() {
  const navigate = useNavigate();
  const [hrData, setHrData] = useState([]);
  const [stressScore, setStressScore] = useState(null);
  const [stressUpdatedAt, setStressUpdatedAt] = useState(null);
  const [isLive, setIsLive] = useState(false);

  // Single load function used on mount AND on each tick. Tracks
  // "is data live" by checking whether the freshest sample is
  // within the chart window — old leftover samples shouldn't count
  // as a live stream.
  const loadData = useRef(async () => {
    const [hr, score] = await Promise.all([fetchHrWindow(), fetchStressScore()]);
    if (hr && hr.length > 0) {
      setHrData(hr);
      const newest = hr[hr.length - 1].timestamp;
      setIsLive(Date.now() - newest < HR_WINDOW_MS);
    } else {
      setHrData([]);
      setIsLive(false);
    }
    if (score !== null) {
      setStressScore(score);
      setStressUpdatedAt(Date.now());
    }
  }).current;

  // Initial load.
  useEffect(() => {
    void loadData();
  }, [loadData]);

  // Poll HR window — short interval since the chart is the centrepiece.
  useEffect(() => {
    const id = setInterval(async () => {
      const hr = await fetchHrWindow();
      if (hr && hr.length > 0) {
        setHrData(hr);
        const newest = hr[hr.length - 1].timestamp;
        setIsLive(Date.now() - newest < HR_WINDOW_MS);
      } else {
        setIsLive(false);
      }
    }, HR_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  // Poll stress score on a slower cadence — the backend recomputes
  // these in batches, no point hammering it every 5s.
  useEffect(() => {
    const id = setInterval(async () => {
      const score = await fetchStressScore();
      if (score !== null) {
        setStressScore(score);
        setStressUpdatedAt(Date.now());
      }
    }, STRESS_REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const latestHr = hrData.length > 0 ? Math.round(hrData[hrData.length - 1].hr) : null;
  const stressDisplay =
    stressScore === null || Math.round(stressScore) === 0 ? '--' : Math.round(stressScore);

  return (
    <div className="sensors-page-wrapper">
      <Navbar />

      <div className="sensors-container">

        {/* ─── Connect-from-phone Banner ─── */}
        <div className="sensors-connect-banner">
          <div className="sensors-connect-icon-wrap">
            <PhoneIcon size={28} color="#FFFFFF" />
          </div>
          <div className="sensors-connect-text">
            <h2 className="sensors-connect-title">Connect from your phone</h2>
            <p className="sensors-connect-desc">
              Sensor pairing is handled by the HRV-4 mobile app. Open the app on your phone, go to <strong>My Sensors</strong>, and tap <strong>Add Device</strong>. Once paired, your live data will stream here automatically.
            </p>
          </div>
          <button
            type="button"
            className="sensors-connect-cta"
            onClick={() => navigate('/faq')}
          >
            <HelpIcon size={14} color="#FFFFFF" />
            <span>Need help?</span>
          </button>
        </div>

        {/* ─── Live Data Grid ─── */}
        <div className="sensors-grid">

          {/* Live HR Chart Card */}
          <div className="sensors-card sensors-chart-card">
            <div className="sensors-card-header">
              <div>
                <h3 className="sensors-card-title">Live HR</h3>
                <span className="sensors-card-subtitle">Last 5 minutes</span>
              </div>
              <div className="sensors-card-value-group">
                <HeartPulseIcon size={18} color="#FF6B6B" />
                <span className="sensors-card-value">
                  {latestHr !== null ? `${latestHr}` : '--'}
                  <span className="sensors-card-unit">bpm</span>
                </span>
              </div>
            </div>

            <LiveHrChart data={hrData} />

            <div className="sensors-card-footer">
              {isLive ? (
                <span className="sensors-status-pill is-live">
                  <ConnectedDot /> Live from sensor
                </span>
              ) : (
                <span className="sensors-status-pill">
                  Waiting for samples — start a measurement on the HRV-4 mobile app
                </span>
              )}
            </div>
          </div>

        </div>

        {/* ─── How to Pair Steps ─── */}
        <div className="sensors-card sensors-steps-card">
          <h3 className="sensors-card-title">How to pair your sensor</h3>
          <span className="sensors-card-subtitle">Quick guide — takes about a minute</span>

          <ol className="sensors-steps-list">
            <li>
              <span className="sensors-step-num">1</span>
              <span>Open the <strong>HRV-4 mobile app</strong> and sign in with the same account.</span>
            </li>
            <li>
              <span className="sensors-step-num">2</span>
              <span>Tap <strong>My Sensors</strong> in the bottom tab bar.</span>
            </li>
            <li>
              <span className="sensors-step-num">3</span>
              <span>Turn on your Polar device and keep it within 1 meter of the phone. Make sure Bluetooth is on.</span>
            </li>
            <li>
              <span className="sensors-step-num">4</span>
              <span>Tap <strong>Add Device</strong> and select your sensor when it appears. Wait for the <strong>Connected</strong> badge.</span>
            </li>
          </ol>

          <button
            type="button"
            className="sensors-steps-faq-btn"
            onClick={() => navigate('/faq')}
          >
            See full FAQ →
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Sensors;